// src/pages/PrebookCheckout.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import CustomerForm from "../components/CustomerForm";
import { isBookingOpen, bookingMessage } from "../utils/bookingLock";

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

/**
 * ✅ BACKEND URL
 * - Uses Render in production
 * - Falls back to localhost for local development
 */
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://aerzen-backend.onrender.com";

/* ---------- LOAD RAZORPAY SCRIPT ---------- */
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PrebookCheckout() {
  const location = useLocation();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);

  /* ---------- BOOKING LOCK ---------- */
  if (!isBookingOpen()) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border rounded-2xl p-6 text-center">
          <h2 className="text-xl font-semibold mb-3">
            Pre-Booking Not Started
          </h2>
          <p className="text-gray-600 mb-6">{bookingMessage()}</p>
          <Button variant="outline" onClick={() => navigate("/")}>
            ← Back to Home
          </Button>
        </div>
      </section>
    );
  }

  /* ---------- LOAD PRODUCT ---------- */
  useEffect(() => {
    if (!location.state) {
      navigate("/products");
      return;
    }
    setProduct(location.state);
  }, [location, navigate]);

  if (!product) return null;

  /* ---------- PRICE ---------- */
  const unitPrebook = Number(product.amount) || 0;
  const unitMrp = Number(product.mrp) || 0;

  const totalPrebook = unitPrebook * quantity;
  const totalMrp = unitMrp * quantity;
  const balance = totalMrp - totalPrebook;

  /* ======================================================
     PAYMENT FLOW (UNCHANGED)
  ====================================================== */
  async function startPayment() {
    if (!RAZORPAY_KEY) {
      alert("Payment configuration error.");
      return;
    }

    setLoading(true);

    try {
      /* 1️⃣ CREATE ORDER */
      const createRes = await fetch(`${BACKEND_URL}/api/orders/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...customer,
          model: product.model,
          quantity,
          unitMrp,
          unitPrebook,
          totalPrebook,
          totalMrp,
        }),
      });

      if (!createRes.ok) throw new Error("Order creation failed");

      const { internalOrderId } = await createRes.json();

      /* 2️⃣ MARK PAYMENT ATTEMPTED */
      await fetch(`${BACKEND_URL}/api/orders/payment-attempted`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ internalOrderId }),
      });

      /* 3️⃣ LOAD RAZORPAY */
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        alert("Payment gateway failed to load.");
        setLoading(false);
        return;
      }

      /* 4️⃣ OPEN RAZORPAY */
      const options = {
        key: RAZORPAY_KEY,
        amount: totalPrebook * 100,
        currency: "INR",

        name: "AERZEN AIR™",
        description: `Pre-Booking | ${product.model} × ${quantity}`,
        image: "/assets/aerzen-logo.png",

        prefill: {
          email: customer.email || "",
          contact: customer.phone || "",
        },

        notes: {
          internalOrderId,
        },

        theme: { color: "#1f4fd8" },

        handler: () => {
          alert("✅ Pre-booking successful!");
          navigate("/");
        },

        modal: {
          ondismiss: () => setLoading(false),
        },
      };

      const rzp = new window.Razorpay(options);

      /* ❌ PAYMENT FAILED / CANCELLED */
      rzp.on("payment.failed", async (response) => {
        try {
          await fetch(`${BACKEND_URL}/api/orders/payment-failed`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              internalOrderId,
              reason:
                response?.error?.description || "User cancelled payment",
            }),
          });
        } catch (e) {
          console.error("Failed to mark payment as failed", e);
        }

        alert("❌ Payment cancelled or failed. No money was deducted.");
        setLoading(false);
      });

      rzp.open();
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  /* ======================================================
     UI (UNCHANGED)
  ====================================================== */
  return (
    <div className="max-w-3xl mx-auto py-20 px-4">
      <h1 className="text-3xl font-bold mb-6 text-center">
        Pre-Book {product.model}
      </h1>

      {!customer && (
        <div className="bg-white rounded-2xl shadow p-8">
          <h2 className="text-xl font-semibold mb-4">
            Enter Your Details
          </h2>
          <CustomerForm onSubmit={setCustomer} />
        </div>
      )}

      {customer && (
        <div className="bg-white rounded-2xl shadow p-8 space-y-4">
          <div><strong>Name:</strong> {customer.name}</div>
          <div><strong>Phone:</strong> {customer.phone}</div>
          <div><strong>City:</strong> {customer.city || "—"}</div>

          <div className="pt-4 border-t">
            <strong>Quantity</strong>
            <div className="flex items-center gap-4 mt-2">
              <Button variant="outline" onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</Button>
              <span className="text-lg font-semibold">{quantity}</span>
              <Button variant="outline" onClick={() => setQuantity(q => Math.min(10, q + 1))}>+</Button>
            </div>
          </div>

          <div className="pt-4 border-t">
            <div className="text-lg font-semibold">
              Total Pre-Booking: ₹{totalPrebook}
            </div>
            <div className="text-sm text-gray-600">
              Balance payable at dispatch: ₹{balance}
            </div>
          </div>

          <Button
            onClick={startPayment}
            disabled={loading}
            className="w-full py-6 bg-[var(--aerzen-blue)]"
          >
            {loading
              ? "Opening Secure Payment…"
              : `Pay ₹${totalPrebook} Securely →`}
          </Button>
        </div>
      )}
    </div>
  );
}
